import { useState, useMemo } from 'react'
import { Link, router } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'
import type { InvestorSummary, InvestorRelationship } from '@/types/investor'

interface Props {
  investors: InvestorSummary[]
}

const RELATIONSHIP_LABELS: Record<InvestorRelationship, string> = {
  family: 'Family',
  business_partner: 'Business Partner',
  fund_investor: 'Fund Investor',
  other: 'Other',
}

type SortBy = 'name' | 'capital' | 'active_loans' | 'distributed'

function availableTaxYears(): number[] {
  const currentYear = new Date().getFullYear()
  return [currentYear, currentYear - 1, currentYear - 2, currentYear - 3]
}

export default function InvestorsIndex({ investors }: Props) {
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('name')
  const years = availableTaxYears()
  const [taxYear, setTaxYear] = useState<number>(years[1] ?? years[0])

  function handleBulkStatements() {
    window.location.href = `/investors/statements.zip?year=${taxYear}`
  }

  function handleK1Export() {
    window.location.href = `/exports/investor_k1_data.csv?year=${taxYear}`
  }

  const filtered = useMemo(() => {
    let result = investors
    if (search) {
      const q = search.toLowerCase()
      result = result.filter(
        (i) =>
          i.name.toLowerCase().includes(q) ||
          i.email?.toLowerCase().includes(q) ||
          i.phone?.includes(q),
      )
    }
    result = [...result].sort((a, b) => {
      switch (sortBy) {
        case 'capital':
          return b.total_capital_committed - a.total_capital_committed
        case 'active_loans':
          return b.active_loan_count - a.active_loan_count
        case 'distributed':
          return b.total_distributed - a.total_distributed
        default:
          return a.name.localeCompare(b.name)
      }
    })
    return result
  }, [investors, search, sortBy])

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Investors</h1>
            <p className="text-sm text-gray-500 mt-1">Capital partners behind your loans</p>
          </div>
          <Link
            href="/investors/new"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Investor
          </Link>
        </div>

        {investors.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-sm font-semibold text-gray-700">Tax year exports</h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Download statements for all investors or export K-1 data for your accountant.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <select
                  value={taxYear}
                  onChange={(e) => setTaxYear(Number(e.target.value))}
                  className="text-xs px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-700"
                >
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleBulkStatements}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
                  </svg>
                  Download all {taxYear} statements (ZIP)
                </button>
                <button
                  type="button"
                  onClick={handleK1Export}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-semibold rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export K-1 data (CSV)
                </button>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              Informational only. LendSolo is a tracking tool, not a tax advisor. Consult your accountant.
            </p>
          </div>
        )}

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, or phone..."
            className="flex-1 w-full sm:w-auto text-sm px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortBy)}
            className="text-xs px-3 py-2 border border-gray-200 rounded-lg bg-white text-gray-600"
          >
            <option value="name">Sort: Name</option>
            <option value="capital">Sort: Capital Committed</option>
            <option value="active_loans">Sort: Active Loans</option>
            <option value="distributed">Sort: Total Distributed</option>
          </select>
        </div>

        {filtered.length === 0 ? (
          <EmptyState hasInvestors={investors.length > 0} />
        ) : (
          <>
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Name</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Relationship</th>
                      <th className="text-left py-3 px-4 font-medium text-gray-500">Contact</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500">Capital Committed</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500">Total Distributed</th>
                      <th className="text-right py-3 px-4 font-medium text-gray-500">Active Loans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((i) => (
                      <tr
                        key={i.id}
                        className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer"
                        onClick={() => router.visit(`/investors/${i.id}`)}
                      >
                        <td className="py-2.5 px-4">
                          <span className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
                            {i.name}
                          </span>
                        </td>
                        <td className="py-2.5 px-4 text-xs text-gray-500">
                          {i.relationship ? RELATIONSHIP_LABELS[i.relationship] : '—'}
                        </td>
                        <td className="py-2.5 px-4 text-xs text-gray-500">
                          {i.email && <span>{i.email}</span>}
                          {i.email && i.phone && <span className="mx-1">&middot;</span>}
                          {i.phone && <span>{i.phone}</span>}
                          {!i.email && !i.phone && <span className="text-gray-300">—</span>}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                          ${i.total_capital_committed.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                          ${i.total_distributed.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-gray-900">
                          {i.active_loan_count}
                          {i.loan_count > i.active_loan_count && (
                            <span className="text-gray-400 text-xs ml-0.5">/{i.loan_count}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="md:hidden space-y-3">
              {filtered.map((i) => (
                <div
                  key={i.id}
                  onClick={() => router.visit(`/investors/${i.id}`)}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-emerald-600 truncate">{i.name}</h3>
                      {(i.email || i.phone) && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{i.email || i.phone}</p>
                      )}
                    </div>
                    {i.relationship && (
                      <span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600 shrink-0">
                        {RELATIONSHIP_LABELS[i.relationship]}
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <div>
                      <span className="text-gray-400">Capital</span>
                      <p className="font-semibold text-gray-900 font-mono">
                        ${i.total_capital_committed.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-400">Distributed</span>
                      <p className="font-semibold text-gray-900 font-mono">
                        ${i.total_distributed.toLocaleString('en-US', { minimumFractionDigits: 0 })}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-400">Active Loans</span>
                      <p className="font-semibold text-gray-900 font-mono">
                        {i.active_loan_count}
                        {i.loan_count > i.active_loan_count && (
                          <span className="text-gray-400"> / {i.loan_count} total</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </AppLayout>
  )
}

function EmptyState({ hasInvestors }: { hasInvestors: boolean }) {
  return (
    <div className="text-center py-16">
      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
        <svg
          className="w-8 h-8 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={1.5}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-1">
        {hasInvestors ? 'No investors match your search' : 'No investors yet'}
      </h3>
      <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto">
        {hasInvestors
          ? 'Try a different search term.'
          : 'Track capital partners and their ownership stakes on each loan.'}
      </p>
      {!hasInvestors && (
        <Link
          href="/investors/new"
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Investor
        </Link>
      )}
    </div>
  )
}
